/**
 * useJobPolling — polls GET /api/jobs/:id for a background generation job (VP-119).
 *
 * Centralizes the safety rails every poller needs so a spinner can never hang
 * forever: a hard overall timeout (POLL_TIMEOUT_MS), a per-request timeout on the
 * GET itself, and a terminal failure signal when the poll request keeps erroring.
 * Pattern lifted from render-stories-button.tsx, adapted to React Query.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes hard stop
// Poll requests are short GET calls — 10s is ample; prevents apiRequest's default
// timeout from surfacing a false error while the job is still running.
const POLL_REQUEST_TIMEOUT_MS = 10_000;

type JobShape = { status: string };

export type JobPollFailure = { kind: "timeout" | "request_error"; message: string };

const isTerminal = (job: JobShape | undefined): boolean =>
  job?.status === "done" || job?.status === "error";

export function useJobPolling<TJob extends JobShape>(
  jobId: string | null
): { job: TJob | undefined; pollFailure: JobPollFailure | null } {
  // Hard timer independent of poll ticks — fires even if every GET hangs/fails.
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    setExpired(false);
    if (!jobId) return;
    const timer = setTimeout(() => setExpired(true), POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [jobId]);

  const query = useQuery<TJob>({
    queryKey: ["job", jobId],
    queryFn: () =>
      apiRequest("GET", `/api/jobs/${jobId}`, undefined, { timeoutMs: POLL_REQUEST_TIMEOUT_MS }),
    enabled: !!jobId,
    // Survive transient blips; after retries the query goes to error state and we stop.
    retry: 2,
    refetchInterval: (q) => {
      if (expired || q.state.status === "error") return false;
      return isTerminal(q.state.data as TJob | undefined) ? false : POLL_INTERVAL_MS;
    },
  });

  let pollFailure: JobPollFailure | null = null;
  if (jobId && !isTerminal(query.data)) {
    if (query.isError) {
      const err = query.error;
      pollFailure = {
        kind: "request_error",
        message: err instanceof Error ? err.message : String(err),
      };
    } else if (expired) {
      pollFailure = { kind: "timeout", message: "timeout" };
    }
  }

  return { job: query.data, pollFailure };
}
